import { forwardRef } from 'react'
import { classNames } from '@/utils/classNames'
import { useSaveButtonColor } from '@/stores/saveButtonColor'

interface SaveButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isSaved: boolean
    variant?: 'default' | 'on-dark'
    size?: 'sm' | 'lg'
    className?: string
    saveText?: string
    savedText?: string
}

/**
 * Darkens a hex color by a percentage for hover states
 */
const darkenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16)
    const r = Math.max(0, Math.floor(((num >> 16) & 0xff) * (1 - percent)))
    const g = Math.max(0, Math.floor(((num >> 8) & 0xff) * (1 - percent)))
    const b = Math.max(0, Math.floor((num & 0xff) * (1 - percent)))
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}

export const SaveButton = forwardRef<HTMLButtonElement, SaveButtonProps>(
    (
        {
            isSaved,
            variant = 'default',
            size = 'lg',
            onClick,
            disabled = false,
            className,
            saveText = 'Save',
            savedText = 'Saved',
            ...props
        },
        ref
    ) => {
        const { color } = useSaveButtonColor()

        // Base structural classes that don't conflict with layout
        const baseClasses =
            'cursor-pointer rounded-xl font-normal whitespace-nowrap transition'

        // Size-specific classes (font-size, padding) - using official Tailwind classes
        const getSizeClasses = () => {
            switch (size) {
                case 'sm':
                    return 'text-sm px-3 py-2' // 12px
                case 'lg':
                default:
                    return 'text-sm px-3 py-3' // 16px
                // case 'md':
                // default:
                //     return 'text-sm px-4 py-2 ' // 14px
            }
        }

        // Dynamic inline styles for unsaved button (uses user's preferred color)
        const getInlineStyles = (): React.CSSProperties | undefined => {
            if (isSaved) return undefined

            return {
                backgroundColor: color,
                transition: 'background-color 0.2s ease',
            }
        }

        // Handle hover state changes for dynamic color
        const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
            if (!isSaved) {
                e.currentTarget.style.backgroundColor = darkenColor(color, 0.15)
            }
        }

        const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
            if (!isSaved) {
                e.currentTarget.style.backgroundColor = color
            }
        }

        // Color and background classes (only for saved state)
        const getBackgroundClasses = () => {
            if (isSaved) {
                // return variant === 'on-dark'
                //     ? 'bg-white hover:bg-gray-100'
                //     : 'bg-black border border-black hover:bg-gray-800'
                // return 'bg-black border border-black hover:bg-gray-800'
                return 'bg-black hover:bg-gray-800'
            }
            return '' // Dynamic color handled via inline styles
        }

        // Text color classes (separated to avoid twMerge conflicts)
        const getTextColorClasses = () => {
            if (isSaved) {
                // return variant === 'on-dark' ? 'text-black' : 'text-white'
                return 'text-white'
            }
            return 'text-white'
        }

        const sizeClasses = getSizeClasses()
        const backgroundClasses = getBackgroundClasses()
        const textColorClasses = getTextColorClasses()

        return (
            <button
                ref={ref}
                type="button"
                onClick={onClick}
                disabled={disabled}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={getInlineStyles()}
                className={classNames(
                    baseClasses,
                    sizeClasses,
                    backgroundClasses,
                    textColorClasses,
                    disabled && 'cursor-not-allowed opacity-50',
                    className
                )}
                {...props}
            >
                {isSaved ? savedText : saveText}
            </button>
        )
    }
)

SaveButton.displayName = 'SaveButton'
